import React from 'react';
import { getTheme, useThemeMode } from '../theme';
import { api, type HuntMedia, type HuntMediaInput, type MarkCalendarEvent } from '../api.ts';
import { readGpsTrackPoints } from '../karta/gpsUtils';
import {
  HUNT_MEDIA_MARGIN_MS, classifyLibraryItems, findLibraryMedia, huntMediaUsesPicker, isAutoEligible, isHuntMediaAvailable, isPreselected, parseMarkArea,
  presentLimitedLibraryPicker, requestLibraryAccess, toHuntMediaInput, uploadLibraryItem,
  type LibraryAccess, type LibraryItem, type MarkArea, type MatchedItem,
} from '../huntMedia';
import { markHuntMediaSaved, rememberHuntWindow, syncHuntMedia } from '../huntMediaSync';
import { syncStatusText, useAutoFetchSupported, useHuntMediaPrefs, useHuntMediaSyncStatus } from '../panels/HuntMediaSettingsSection';
import { Switch } from '../components/ui';
import { ReportMenu } from '../components/moderation/ReportMenu';

/** Söks automatiskt när jaktsammanfattningen öppnas så här snart efter jakten. */
const AUTO_SCAN_WITHIN_MS = 2 * 60 * 60 * 1000;

const fmtTime = (ms: number) => new Date(ms).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
const fmtDuration = (s: number) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`;

const AREA_LABEL: Record<MatchedItem['area'], string | null> = { inside: null, near: 'nära marken', outside: 'utanför marken', unknown: 'okänd plats' };

/**
 * Bilder och video från jakten. På iPhone hittas det som togs under jakten i Bilder (även Ray-Ban
 * Meta-klipp som Meta AI-appen sparat där); jägaren bockar i och det laddas upp till jakten.
 * A3: det som togs på eller nära marken är förvalt; med "Hämta jaktbilder automatiskt" på laddas
 * det upp av sig självt (huntMediaSync.ts) och syns här medan det väntar eller går iväg.
 * På Android väljer jägaren själv i systemets bildväljare (ingen automatisk sökning).
 */
export function HuntMediaSection({ event }: { event: MarkCalendarEvent }) {
  const { mode } = useThemeMode(); const T = getTheme(mode);
  const eventId = event.id === 'snabbstart' ? null : event.id;
  const startMs = new Date(event.startTime).getTime();
  const endMs = React.useMemo(() => (event.endedAt ? new Date(event.endedAt).getTime() : Date.now()), [event.endedAt]);
  const from = startMs - HUNT_MEDIA_MARGIN_MS;
  const to = endMs + HUNT_MEDIA_MARGIN_MS;
  const native = isHuntMediaAvailable();
  const picker = huntMediaUsesPicker();
  const autoSupported = useAutoFetchSupported() && !picker;
  const [prefs, setPrefs] = useHuntMediaPrefs();
  const sync = useHuntMediaSyncStatus();
  const autoOn = autoSupported && prefs.auto === 'on';

  const [area, setArea] = React.useState<MarkArea>({ rings: [], center: null });
  const areaRef = React.useRef(area);
  const [saved, setSaved] = React.useState<HuntMedia[]>([]);
  const [found, setFound] = React.useState<MatchedItem[] | null>(null);
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [access, setAccess] = React.useState<LibraryAccess | null>(null);
  const [scanning, setScanning] = React.useState(false);
  const [progress, setProgress] = React.useState<{ done: number; total: number } | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const loadSaved = React.useCallback(
    () => api.huntMedia(event.markId, from, to).then(setSaved).catch(() => {}),
    [event.markId, from, to],
  );

  const scan = React.useCallback(async (savedNow: HuntMedia[]) => {
    setScanning(true); setError(null);
    try {
      const status = await requestLibraryAccess();
      setAccess(status);
      if (status !== 'authorized' && status !== 'limited') { setFound([]); return; }
      const have = new Set(savedNow.map(m => m.sourceId).filter(Boolean));
      if (picker) {
        const items = classifyLibraryItems((await findLibraryMedia(from, to)).filter(i => !have.has(i.id)), areaRef.current, readGpsTrackPoints());
        // Stängd bildväljare = inget valt, inget att säga.
        setFound(items.length === 0 ? null : items);
        setSelected(new Set(items.map(i => i.id)));
      } else {
        const items = classifyLibraryItems((await findLibraryMedia(from, to)).filter(i => !have.has(i.id)), areaRef.current, readGpsTrackPoints());
        setFound(items);
        setSelected(new Set(items.filter(isPreselected).map(i => i.id)));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : picker ? 'Kunde inte öppna bildväljaren' : 'Kunde inte söka i Bilder');
    } finally {
      setScanning(false);
    }
  }, [from, to, picker]);

  React.useEffect(() => {
    let cancelled = false;
    const markArea = native
      ? api.markArea(event.markId).then(m => {
        const a = parseMarkArea(m?.coordinatesJson, { lat: m?.mapLat, lng: m?.mapLng });
        areaRef.current = a;
        if (!cancelled) setArea(a);
        // Jaktens fönster sparas, så att automatisk hämtning (om den är på) tar bilderna härifrån också.
        rememberHuntWindow({
          key: eventId ?? `snabbstart:${event.markId}:${startMs}`, markId: event.markId, markName: m?.name, eventId,
          from: startMs, to: event.endedAt ? endMs : null, coordinatesJson: m?.coordinatesJson, mapLat: m?.mapLat, mapLng: m?.mapLng,
        });
      }).catch(() => {})
      : Promise.resolve();
    Promise.all([api.huntMedia(event.markId, from, to), markArea]).then(([list]) => {
      if (cancelled) return;
      setSaved(list);
      if (native && !picker && Date.now() - endMs < AUTO_SCAN_WITHIN_MS) scan(list);
    }).catch(() => { if (!cancelled && native && !picker && Date.now() - endMs < AUTO_SCAN_WITHIN_MS) scan([]); });
    return () => { cancelled = true; };
  }, [event.markId, from, to]); // eslint-disable-line

  // När den automatiska hämtningen blivit klar: visa det uppladdade och sök om det som är kvar att välja.
  const prevSyncState = React.useRef(sync.state);
  React.useEffect(() => {
    const was = prevSyncState.current;
    prevSyncState.current = sync.state;
    if (was === 'uploading' && sync.state !== 'uploading') {
      void api.huntMedia(event.markId, from, to).then(list => { setSaved(list); if (found !== null) void scan(list); }).catch(() => {});
    }
  }, [sync.state]); // eslint-disable-line

  const importSelected = async () => {
    const items = pickable.filter(i => selected.has(i.id));
    if (items.length === 0) return;
    // Spåret finns bara på den här telefonen och sparas i 3 h, så positionerna räknas ut nu.
    const track = readGpsTrackPoints();
    const inputs: HuntMediaInput[] = [];
    const failed: MatchedItem[] = [];
    setError(null);
    setProgress({ done: 0, total: items.length });
    for (const item of items) {
      try {
        inputs.push(toHuntMediaInput(item, await uploadLibraryItem(item.id), track));
      } catch {
        failed.push(item);
      }
      setProgress(p => p && { ...p, done: p.done + 1 });
    }
    try {
      if (inputs.length > 0) {
        await api.addHuntMedia(event.markId, eventId, inputs);
        markHuntMediaSaved(event.markId, inputs);
      }
    } catch {
      failed.push(...items.filter(i => inputs.some(x => x.sourceId === i.id)));
    }
    setProgress(null);
    setFound(failed);
    setSelected(new Set(failed.map(i => i.id)));
    if (failed.length > 0) setError(`${failed.length} av ${items.length} kunde inte laddas upp. Försök igen.`);
    loadSaved();
  };

  const toggle = (id: string) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  // Det som den automatiska hämtningen tar hand om visas i den egna rutan och inte i väljaren.
  const autoQueue = autoOn ? sync.queue.filter(q => q.markId === event.markId && q.item.takenAt >= from && q.item.takenAt <= to) : [];
  const queuedIds = new Set(autoQueue.map(q => q.item.id));
  const pickable = (found || []).filter(i => !queuedIds.has(i.id));
  const nVideo = pickable.filter(i => i.type === 'video').length;
  const nImage = pickable.length - nVideo;
  const autoCandidates = (found || []).filter(i => isAutoEligible(i, area)).length;
  const askAuto = autoSupported && prefs.auto === null && autoCandidates > 0 && !progress;

  const turnOnAuto = () => { setPrefs({ auto: 'on' }); void syncHuntMedia({ force: false }); };
  const addMorePhotos = async () => { await presentLimitedLibraryPicker().catch(() => {}); await scan(saved); };
  const foundLabel = [nVideo > 0 && `${nVideo} klipp`, nImage > 0 && `${nImage} ${nImage === 1 ? 'bild' : 'bilder'}`].filter(Boolean).join(' och ');

  if (!native && saved.length === 0) return null;

  const btn: React.CSSProperties = { padding: '7px 14px', borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' };
  const tile: React.CSSProperties = { position: 'relative', display: 'block', width: '100%', aspectRatio: '1', borderRadius: 6, overflow: 'hidden', background: T.bg, border: `1px solid ${T.border}` };
  const badge: React.CSSProperties = { position: 'absolute', bottom: 4, right: 4, fontSize: 10, fontWeight: 700, color: '#fff', background: 'rgba(0,0,0,0.6)', padding: '1px 6px', borderRadius: 6 };

  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ padding: '9px 14px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 26, height: 26, borderRadius: 5, background: 'rgba(238,143,9,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: T.amber, fontSize: 13 }}>B</div>
        <div style={{ fontWeight: 700, color: T.text, fontSize: 14, flex: 1 }}>Bilder och video</div>
        <span style={{ fontSize: 11, fontWeight: 700, color: T.amber, background: 'rgba(238,143,9,0.12)', padding: '2px 9px', borderRadius: 10 }}>{saved.length} st</span>
      </div>
      <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>

        {saved.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 6 }}>
            {saved.map(m => (
              <div key={m.id} style={{ position: 'relative' }}>
                <a href={m.url} target="_blank" rel="noopener noreferrer" style={tile} title={m.lat != null ? `${m.lat.toFixed(5)}, ${m.lng?.toFixed(5)}` : undefined}>
                  {m.mediaType === 'video'
                    ? <video src={`${m.url}#t=0.1`} preload="metadata" muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <img src={m.url} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                  <span style={badge}>{m.mediaType === 'video' ? '▶ ' : ''}{fmtTime(m.takenAt)}</span>
                </a>
                {/* AS1c: rapportera/blockera. API:t säger till om det är ens egen bild. */}
                <span style={{ position: 'absolute', top: 2, right: 2 }}>
                  <ReportMenu target={{ type: 'hunt_media', id: m.id }} ownerName="den som laddade upp" noun={m.mediaType === 'video' ? 'videon' : 'bilden'} onDark
                    onBlocked={() => { void loadSaved(); }} />
                </span>
              </div>
            ))}
          </div>
        )}

        {askAuto && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 10, borderRadius: 6, background: T.bg, border: `1px solid ${T.border}` }}>
            <div style={{ fontWeight: 700, color: T.text, fontSize: 13 }}>Hämta jaktbilder automatiskt i fortsättningen?</div>
            <div style={{ color: T.textSub, fontSize: 12, lineHeight: 1.5 }}>
              Det du fotar eller filmar under en jakt, på eller nära marken, läggs då i jaktens album utan att du behöver välja.
              Bilderna görs mindre och skickas bara på wifi. Annat i Bilder rörs inte. Du kan ändra det under Inställningar.
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" onClick={turnOnAuto} style={{ ...btn, background: T.amber, color: '#000', border: 'none' }}>Ja, hämta automatiskt</button>
              <button type="button" onClick={() => setPrefs({ auto: 'off' })} style={{ ...btn, background: 'none', color: T.textSub, border: `1px solid ${T.border}` }}>Nej, jag väljer själv</button>
            </div>
          </div>
        )}

        {autoOn && autoQueue.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 10, borderRadius: 6, background: 'rgba(238,143,9,0.06)', border: '1px solid rgba(238,143,9,0.22)' }}>
            <div style={{ fontWeight: 700, color: T.text, fontSize: 13 }}>Hämtas automatiskt till jakten</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))', gap: 6 }}>
              {autoQueue.map(({ item: i }) => (
                <div key={i.id} style={{ ...tile, opacity: sync.currentId === i.id ? 1 : 0.8, outline: sync.currentId === i.id ? `2px solid ${T.amber}` : 'none' }}>
                  {i.thumbnail && <img src={i.thumbnail} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                  <span style={badge}>{i.type === 'video' ? `▶ ${fmtDuration(i.duration)}` : fmtTime(i.takenAt)}</span>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ color: T.textSub, fontSize: 12, flex: 1, minWidth: 160 }}>{syncStatusText(sync)}</span>
              {sync.state === 'waitingForWifi' && (
                <button type="button" onClick={() => void syncHuntMedia({ force: true })} style={{ ...btn, background: T.bg, color: T.text, border: `1px solid ${T.border}` }}>Ladda upp nu</button>
              )}
            </div>
          </div>
        )}

        {native && found && pickable.length > 0 && !progress && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 10, borderRadius: 6, background: 'rgba(238,143,9,0.06)', border: '1px solid rgba(238,143,9,0.22)' }}>
            <div style={{ fontWeight: 700, color: T.text, fontSize: 13 }}>{foundLabel} {picker ? 'valda' : 'från jakten'} – lägg till?</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))', gap: 6 }}>
              {pickable.map(i => {
                const on = selected.has(i.id);
                const where = AREA_LABEL[i.area];
                return (
                  <button key={i.id} type="button" onClick={() => toggle(i.id)} style={{ ...tile, padding: 0, cursor: 'pointer', opacity: on ? 1 : 0.45, outline: on ? `2px solid ${T.amber}` : 'none' }}>
                    {i.thumbnail && <img src={i.thumbnail} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                    <span style={{ position: 'absolute', top: 4, left: 4, width: 16, height: 16, borderRadius: 8, background: on ? T.amber : 'rgba(0,0,0,0.45)', color: '#000', fontSize: 11, fontWeight: 800, lineHeight: '16px' }}>{on ? '✓' : ''}</span>
                    {where && i.area !== 'inside' && area.rings.length + (area.center ? 1 : 0) > 0 && (
                      <span style={{ ...badge, bottom: 'auto', top: 4, right: 4, background: i.area === 'outside' ? 'rgba(120,20,20,0.75)' : 'rgba(0,0,0,0.6)' }}>{where}</span>
                    )}
                    <span style={badge}>{i.type === 'video' ? `▶ ${fmtDuration(i.duration)}` : fmtTime(i.takenAt)}</span>
                  </button>
                );
              })}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button type="button" onClick={importSelected} disabled={pickable.every(i => !selected.has(i.id))} style={{ ...btn, background: T.amber, color: '#000', border: 'none', opacity: pickable.every(i => !selected.has(i.id)) ? 0.45 : 1 }}>
                Lägg till ({pickable.filter(i => selected.has(i.id)).length})
              </button>
              <button type="button" onClick={() => setFound(null)} style={{ ...btn, background: 'none', color: T.textSub, border: `1px solid ${T.border}` }}>Inte nu</button>
            </div>
          </div>
        )}

        {progress && (
          <div style={{ color: T.text, fontSize: 13 }}>Laddar upp {Math.min(progress.done + 1, progress.total)} av {progress.total}…</div>
        )}

        {native && found && pickable.length === 0 && autoQueue.length === 0 && !scanning && access !== 'denied' && (
          <div style={{ color: T.textSub, fontSize: 12 }}>Inga nya bilder eller klipp från {fmtTime(from)}–{fmtTime(to)} i Bilder.</div>
        )}
        {access === 'denied' && (
          <div style={{ color: T.textSub, fontSize: 12 }}>venari har inte åtkomst till Bilder. Tillåt det under Inställningar → venari → Bilder.</div>
        )}
        {access === 'limited' && (
          <div style={{ color: T.textSub, fontSize: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ flex: 1, minWidth: 180 }}>Du har gett begränsad åtkomst, så bara bilder du valt ut syns här{autoOn ? ' och hämtas automatiskt' : ''}.</span>
            {autoSupported && <button type="button" onClick={addMorePhotos} style={{ ...btn, background: T.bg, color: T.text, border: `1px solid ${T.border}` }}>Välj fler bilder</button>}
          </div>
        )}
        {error && <div style={{ color: '#E53935', fontSize: 12 }}>{error}</div>}

        {native && !progress && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button type="button" onClick={() => scan(saved)} disabled={scanning} style={{ ...btn, background: T.bg, color: T.text, border: `1px solid ${T.border}` }}>
              {picker
                ? (scanning ? 'Öppnar bildväljaren…' : found === null ? 'Välj bilder och video från jakten' : 'Välj fler')
                : (scanning ? 'Söker i Bilder…' : found === null ? 'Hämta bilder och video från telefonen' : 'Hämta fler')}
            </button>
            <span style={{ color: T.textMuted, fontSize: 11, flex: 1, minWidth: 180 }}>
              {picker
                ? `Välj det du tog under jakten (${fmtTime(from)}–${fmtTime(to)}). Positionen hämtas från bilden eller ditt spår.`
                : 'Klipp från Ray-Ban Meta syns när Meta AI-appen har sparat dem i Bilder. Det sker när glasögonen är anslutna till telefonen.'}
            </span>
          </div>
        )}

        {autoSupported && prefs.auto !== null && !progress && (
          <Switch checked={autoOn} onChange={v => setPrefs({ auto: v ? 'on' : 'off' })} label="Hämta jaktbilder automatiskt" labelPosition="right"
            style={{ fontSize: 12, color: T.textSub }} />
        )}
      </div>
    </div>
  );
}
